import type { FC } from 'react';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Stack } from '@mui/material';
import {
  NoReceiptsAdded,
  ReceiptBatchDetailsReceipts,
} from './ReceiptBatchDetailsReceipts';
import type { BatchReceiptDetails } from './ReceiptBatchForm';

import { ReceiptBatchImagePreview } from './ReceiptBatchImagePreview';
import { TestIds } from '../testIds';
import type { UseReceiptImagesType } from '@pflegenavi/shared-frontend/platform';
import { EnlargeImageGallery } from '@pflegenavi/transaction-components';
import type { UrlTypeNameMapping } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import type { GetReceiptBatchPaymentPhoenixResult } from '@pflegenavi/frontend/api-nursing-home';
import { MangopayPaymentStatus } from '@pflegenavi/frontend/api-nursing-home';
import { ReceiptBatchUnpaidReceiptsWarning } from '../components/ReceiptBatchUnpaidReceiptsWarning';
import { isSettlementReceiptState } from '@pflegenavi/shared/api';

interface ReceiptBatchReadOnlyPageProps {
  isLoading: boolean;
  receipts: BatchReceiptDetails[];
  autoGenerateReceipts?: boolean;
  autoGenerateReceiptsPreview?: (entryId: string) => Promise<Blob | undefined>;
  useReceiptImages: UseReceiptImagesType;
  ReceiptBatchSummaryCard: React.ReactNode;
  CashLink?: React.ReactNode;
  ReceiptBatchPaymentCard?: React.ReactNode;
  width?: number | string;
  payment?: GetReceiptBatchPaymentPhoenixResult | undefined;
}

export const ReceiptBatchDetails: FC<ReceiptBatchReadOnlyPageProps> = ({
  isLoading,
  receipts,
  autoGenerateReceipts,
  autoGenerateReceiptsPreview,
  useReceiptImages,
  ReceiptBatchSummaryCard,
  CashLink,
  width,
  ReceiptBatchPaymentCard,
  payment,
}) => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);

  const activeReceiptImageIds = useMemo(() => {
    return receipts[activeIndex]?.receiptImageIds?.filter((id) => !!id);
  }, [activeIndex, receipts]);

  const [generatedReceiptImageUrls, setGeneratedReceiptImageUrls] = useState<
    string[]
  >([]);
  const [generatedReceiptTypeMap, setGeneratedReceiptTypeMap] =
    useState<UrlTypeNameMapping>(new Map());

  const generateReceiptPreviewCallback = useCallback(async () => {
    const receipt = receipts[activeIndex];
    if (receipt && autoGenerateReceiptsPreview) {
      const blob = await autoGenerateReceiptsPreview(receipt.id!);
      if (blob) {
        const file = window.URL.createObjectURL(blob);

        setGeneratedReceiptImageUrls([file]);
        setGeneratedReceiptTypeMap(
          new Map([
            [
              file,
              {
                type: 'application/pdf',
                data: Promise.resolve(blob),
                name: `${t('receipts.batch.receipt-preview-file')}-${
                  receipt.residentName
                }.pdf`,
              },
            ],
          ])
        );
      }
    }
  }, [autoGenerateReceiptsPreview, activeIndex, receipts, t]);

  const unpaidReceiptBatchEntries = useMemo(() => {
    return payment?.data.receipt_batch_payment.receipt_batch_entries.filter(
      (entry) =>
        entry.status === MangopayPaymentStatus.Pending &&
        // Receipts in settlement are handled outside of the payment flow, so
        // they are not "could not be paid" entries.
        !isSettlementReceiptState(entry.receipt_state)
    );
  }, [payment?.data.receipt_batch_payment.receipt_batch_entries]);

  return (
    <Stack
      direction="row"
      data-testid={TestIds.ReceiptBatchDetail}
      id="receiptBatchdetail"
      sx={{
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Stack
        sx={{
          gap: 2,
          flexGrow: 1,
        }}
      >
        {unpaidReceiptBatchEntries && unpaidReceiptBatchEntries.length !== 0 ? (
          <ReceiptBatchUnpaidReceiptsWarning
            unpaidReceiptBatchEntries={unpaidReceiptBatchEntries}
            audience="nursing-home"
          />
        ) : null}
        <Stack
          direction="row"
          sx={{
            gap: 2,
          }}
        >
          {receipts.length === 0 && !isLoading ? (
            <NoReceiptsAdded />
          ) : (
            <>
              <ReceiptBatchDetailsReceipts
                receipts={receipts}
                receiptsPayments={
                  payment?.data.receipt_batch_payment.receipt_batch_entries ??
                  []
                }
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                autoGenerateReceipts={autoGenerateReceipts ?? false}
              />
              <ReceiptBatchImagePreview
                receiptImageIds={activeReceiptImageIds}
                useReceiptImages={useReceiptImages}
                autoGenerateReceipts={autoGenerateReceipts}
                autoGenerateReceiptsPreview={
                  autoGenerateReceiptsPreview
                    ? generateReceiptPreviewCallback
                    : undefined
                }
              />
              <EnlargeImageGallery
                imageUrls={generatedReceiptImageUrls}
                handleClose={() => {
                  setGeneratedReceiptImageUrls([]);
                }}
                open={generatedReceiptImageUrls.length > 0}
                initialSelectedIndex={0}
                imageUrlToTypeMap={generatedReceiptTypeMap}
              />
            </>
          )}

          <Stack
            sx={{
              gap: 2,
              width: width,
            }}
          >
            {ReceiptBatchSummaryCard}

            {CashLink}

            {ReceiptBatchPaymentCard}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
